<template>
  <lb-drawer
    :value="value"
    :heading="dialogHeading"
    :width="drawerWidths.sm"
    :Maxwidth="drawerWidths.sm"
    :loading="loading"
    data-testid="setup-location__drawer"
    @input="onInput"
  >
    <template #body>
      <div class="location-form-dialog" data-testid="setup-location__drawer-body">
        <h5
          class="location-form-dialog__section-title location-form-dialog__section-title--required"
          data-testid="setup-location__drawer-details-title"
        >
          Details
        </h5>

        <div class="location-form-dialog__field" data-testid="setup-location__drawer-col-name">
          <p class="location-form-dialog__label location-form-dialog__label--required ma-0">
            {{ locationHeaderLabel }} Name
          </p>
          <lb-string
            :value="form.name"
            label=""
            :error="errors.name"
            :placeholder="`Select ${locationHeaderLabel}`"
            data-testid="setup-location__drawer-name-input"
            @input="updateField('name', $event)"
          />
        </div>

        <div class="location-form-dialog__row" data-testid="setup-location__drawer-row-country-type">
          <div class="location-form-dialog__row-item" data-testid="setup-location__drawer-col-country">
            <p class="location-form-dialog__label location-form-dialog__label--required ma-0">
              {{ locationHeaderLabel }} Country
            </p>
            <lb-dropdown
              :value="form.country"
              :items="countryList"
              label=""
              :error="errors.country"
              :attach="false"
              itemtext="name"
              itemvalue="value"
              appendiconinner="mdi-chevron-down"
              placeholder="Select Country"
              data-testid="setup-location__drawer-country-input"
              @input="updateField('country', $event)"
            />
          </div>

          <div class="location-form-dialog__row-item" data-testid="setup-location__drawer-col-type">
            <p class="location-form-dialog__label location-form-dialog__label--required ma-0">
              {{ locationHeaderLabel }} Type
            </p>
            <lb-dropdown
              :value="form.type"
              :items="locationTypes"
              label=""
              :error="errors.type"
              :attach="false"
              itemtext="name"
              itemvalue="value"
              appendiconinner="mdi-chevron-down"
              placeholder="Select Type"
              data-testid="setup-location__drawer-type-input"
              @input="onTypeChange"
            />
          </div>
        </div>

        <div
          v-if="showNewTypeField"
          class="location-form-dialog__field"
          data-testid="setup-location__drawer-col-newtype"
        >
          <p class="location-form-dialog__label ma-0">
            Add New {{ locationHeaderLabel }} Type
          </p>
          <lb-string
            :value="form.newtype"
            label=""
            placeholder="Enter Type..."
            :error="errors.newtype"
            data-testid="setup-location__drawer-newtype-input"
            @input="updateField('newtype', $event)"
          />
        </div>

        <div
          class="location-form-dialog__threshold"
          data-testid="setup-location__drawer-col-set-threshold"
        >
          <div class="location-form-dialog__threshold-header">
            <div class="location-form-dialog__threshold-copy">
              <p
                class="location-form-dialog__threshold-title ma-0"
                data-testid="setup-location__drawer-set-threshold-label"
              >
                Set Threshold
              </p>
              <p class="location-form-dialog__threshold-desc ma-0">
                {{ locationHeaderLabel }} specific threshold applies and prevents approval until all differences
                above the limit are explained.
              </p>
            </div>

            <v-switch
              class="location-form-dialog__threshold-switch mt-0 pt-0"
              inset
              dense
              hide-details
              color="primary"
              :input-value="!!form.setThreshold"
              :disabled="loading"
              data-testid="setup-location__drawer-set-threshold-switch"
              @change="onSetThresholdChange"
            />
          </div>

          <div
            v-if="form.setThreshold"
            class="location-form-dialog__field location-form-dialog__field--threshold"
            data-testid="setup-location__drawer-col-threshold"
          >
            <p class="location-form-dialog__label location-form-dialog__label--required ma-0">
              Threshold
            </p>
            <lb-number
              :value="form.threshold"
              label=""
              :error="errors.threshold"
              min="0"
              data-testid="setup-location__drawer-threshold-input"
              @input="updateField('threshold', $event)"
            />
          </div>
        </div>

        <div class="location-form-dialog__field" data-testid="setup-location__drawer-col-description">
          <p class="location-form-dialog__label location-form-dialog__label--required ma-0">
            {{ locationHeaderLabel }} Description
          </p>
          <lb-textBox
            requriedrows="3"
            :value="form.description"
            label=""
            placeholder="Enter description"
            :error="errors.description"
            clearable
            data-testid="setup-location__drawer-description-input"
            @input="updateField('description', $event)"
          />
        </div>
      </div>
    </template>

    <template #actions>
      <v-spacer />
      <v-btn
        color="primary"
        class="location-form-dialog__save-btn text-none white--text"
        :loading="loading"
        :disabled="loading"
        data-testid="setup-location__drawer-save-btn"
        @click="$emit('save')"
      >
        <span data-testid="setup-location__drawer-save-label">{{ saveLabel }}</span>
      </v-btn>
    </template>
  </lb-drawer>
</template>

<script>
import './LocationFormDialog.scss';
import { drawerWidths } from '@/styles/design-tokens.js';

export default {
  name: 'LocationFormDialog',

  data() {
    return { drawerWidths };
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'add',
      validator: (v) => ['add', 'edit'].includes(v),
    },
    form: {
      type: Object,
      default: () => ({}),
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    countryList: {
      type: Array,
      default: () => [],
    },
    locationTypes: {
      type: Array,
      default: () => [],
    },
    locationHeaderLabel: {
      type: String,
      default: 'Location',
    },
  },

  computed: {
    isEdit() {
      return this.mode === 'edit';
    },

    dialogHeading() {
      return this.isEdit ? 'Edit' : `New ${this.locationHeaderLabel}`;
    },

    saveLabel() {
      return this.isEdit ? 'Update' : 'Add';
    },

    showNewTypeField() {
      return this.form?.type === 'Other';
    },
  },

  methods: {
    onInput(open) {
      this.$emit('input', open);
    },

    updateField(key, value) {
      this.$emit('update:form', { ...this.form, [key]: value });
    },

    onTypeChange(type) {
      const next = { ...this.form, type };
      if (type !== 'Other') {
        next.newtype = '';
      }
      this.$emit('update:form', next);
    },

    onSetThresholdChange(enabled) {
      const next = {
        ...this.form,
        setThreshold: !!enabled,
      };
      if (!enabled) {
        next.threshold = null;
      } else if (next.threshold == null || next.threshold === '') {
        next.threshold = 0;
      }
      this.$emit('update:form', next);
    },
  },
};
</script>
